import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { getHot, searchCatalog } from "@/lib/catalog";
import { TYPE_LABELS } from "@/types/movie";
import type { MovieType } from "@/types/movie";

const TYPE_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "全部类型" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "variety", label: "综艺" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentMeta(
    query ? `“${query}”的搜索结果 - 亚洲视频一区` : "搜索影视 - 亚洲视频一区",
    query
      ? `亚洲视频一区关于“${query}”的影视搜索结果，涵盖电影、电视剧与综艺节目，支持按类型与年份进一步筛选。`
      : "在亚洲视频一区搜索电影、电视剧与综艺节目，输入关键词即可快速找到想看的影视内容。",
    "亚洲视频一区,影视搜索,在线影视,电影搜索,电视剧搜索"
  );

  const baseResults = useMemo(() => searchCatalog(query), [query]);

  const years = useMemo(
    () => Array.from(new Set(baseResults.map((m) => m.year))).sort((a, b) => b - a),
    [baseResults]
  );

  const results = useMemo(
    () =>
      baseResults.filter(
        (m) =>
          (typeFilter === "all" || m.type === (typeFilter as MovieType)) &&
          (yearFilter === "all" || String(m.year) === yearFilter)
      ),
    [baseResults, typeFilter, yearFilter]
  );

  const hot = getHot(5);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setTypeFilter("all");
    setYearFilter("all");
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-10">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-500 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">搜索</span>
      </nav>

      <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mb-2">
        {query ? "搜索结果" : "搜索影视"}
      </h1>
      <p className="text-sm text-foreground-600 mb-6">
        {query ? (
          <>
            关键词：<span className="text-primary-500 font-medium">“{query}”</span> · 共匹配到{" "}
            <span className="text-primary-500 font-heading font-bold">{baseResults.length}</span> 部内容
          </>
        ) : (
          "输入影片名称、类型或地区关键词，快速找到你想看的内容。"
        )}
      </p>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 bg-background-100 border border-background-300/60 rounded-md pl-4 pr-2 py-1.5 focus-within:border-primary-500/70 transition-colors">
          <i className="ri-search-line text-foreground-500 text-lg" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="search"
            placeholder="搜索影片、剧集、综艺"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none py-2"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {query && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                className={`px-3 py-1.5 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  typeFilter === f.value
                    ? "bg-secondary-500 text-background-50 font-medium"
                    : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {years.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setYearFilter("all")}
                className={`px-3 py-1.5 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  yearFilter === "all"
                    ? "bg-secondary-500 text-background-50 font-medium"
                    : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
                }`}
              >
                全部年份
              </button>
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setYearFilter(String(y))}
                  className={`px-3 py-1.5 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    yearFilter === String(y)
                      ? "bg-secondary-500 text-background-50 font-medium"
                      : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {query && results.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
          {results.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      )}

      {query && results.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-16 rounded-lg bg-background-100 border border-background-300/50">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h3 className="font-heading text-lg font-semibold text-foreground-900">
            没有找到与“{query}”相关的内容
          </h3>
          <p className="mt-2 text-sm text-foreground-600 max-w-md">
            换个关键词试试，或者从下面这些热门影视里挑一部开始观看。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {(baseResults.length > 0 ? [] : hot).map((m) => (
              <Link
                key={m.id}
                to={`/detail/${m.id}`}
                className="px-3 py-1.5 rounded-md bg-background-200/70 text-foreground-800 text-sm hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap"
              >
                {m.title}
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      )}

      {!query && (
        <div className="rounded-lg bg-background-100 border border-background-300/50 p-4 md:p-6">
          <SectionHeading
            icon="ri-fire-line"
            title="大家都在看"
            subtitle="热门影视一键直达"
            moreTo="/category/movie"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {hot.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <p className="text-xs text-foreground-500 mt-6">
            演示数据提示：本页所有搜索结果均来自演示数据，用于展示搜索与筛选功能。
          </p>
        </div>
      )}

      {query && (
        <p className="text-center text-xs text-foreground-500 mt-8">
          搜索结果基于演示数据生成 · 匹配标题、简介、类型与地区关键词
        </p>
      )}
    </div>
  );
}